import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from '../../../core/data/routes';
import { useAuthStore } from '../store/authStore';
import {
  scheduleFor, religiousAffiliations, genders,
  therapyType, expertiseIn, ageGroup, yesNo,
} from '../../../core/data/enums';
import { AppBar, LoadingWidget, FadeInAnimation } from '../../../core/components/ui';
import { Preferences } from '../../../core/types';
import toast from 'react-hot-toast';

/* ─── Dynamic style helpers (functions can't live in a CSSProperties record) ── */
const chipStyle = (active: boolean): React.CSSProperties => ({
  padding: '7px 14px',
  borderRadius: 99,
  border: active
    ? '1px solid var(--color-primary)'
    : '1px solid var(--color-outline-variant)',
  background: active ? 'var(--color-primary)' : 'var(--color-surface-variant)',
  color: active ? 'white' : 'var(--color-on-surface-variant)',
  fontSize: 13,
  fontWeight: active ? 500 : 400,
  cursor: 'pointer',
  transition: 'all 0.15s',
  userSelect: 'none',
});

const swTrackStyle = (on: boolean): React.CSSProperties => ({
  width: 44,
  height: 24,
  borderRadius: 99,
  border: 'none',
  cursor: 'pointer',
  position: 'relative',
  flexShrink: 0,
  transition: 'background 0.2s',
  background: on ? 'var(--color-primary)' : 'var(--color-outline)',
});

const swThumbStyle = (on: boolean): React.CSSProperties => ({
  position: 'absolute',
  top: 3,
  left: on ? 23 : 3,
  width: 18,
  height: 18,
  borderRadius: '50%',
  background: 'white',
  transition: 'left 0.2s',
  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
});

/* ─── Static styles ──────────────────────────────────────────────────── */
const S: Record<string, React.CSSProperties> = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    background: 'var(--color-surface)',
  },
  appBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '14px 24px',
    borderBottom: '1px solid var(--color-outline-variant)',
    position: 'sticky',
    top: 0,
    background: 'var(--color-surface)',
    zIndex: 10,
  },
  appBarTitle: {
    fontSize: 17,
    fontWeight: 600,
    color: 'var(--color-on-surface)',
  },
  backBtn: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    border: '1px solid var(--color-outline-variant)',
    background: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: 'var(--color-on-surface-variant)',
    flexShrink: 0,
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '28px 24px 56px',
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    maxWidth: 960,
    width: '100%',
    margin: '0 auto',
    boxSizing: 'border-box',
  },
  lead: {
    fontSize: 14,
    color: 'var(--color-on-surface-variant)',
    lineHeight: 1.6,
  },
  grid2: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: 16,
  },
  sectionCard: {
    background: 'var(--color-surface)',
    border: '1px solid var(--color-outline-variant)',
    borderRadius: 16,
    padding: '18px 20px',
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: 'uppercase' as const,
    color: 'var(--color-primary)',
    marginBottom: 4,
  },
  sectionSub: {
    fontSize: 13,
    color: 'var(--color-on-surface-variant)',
    marginBottom: 12,
  },
  textInput: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: 10,
    border: '1px solid var(--color-outline-variant)',
    background: 'var(--color-surface-variant)',
    color: 'var(--color-on-surface)',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 8,
  },
  togglesCard: {
    background: 'var(--color-surface)',
    border: '1px solid var(--color-outline-variant)',
    borderRadius: 16,
    overflow: 'hidden',
  },
  toggleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 20px',
    borderBottom: '1px solid var(--color-outline-variant)',
  },
  toggleRowLast: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 20px',
  },
  toggleLabel: {
    fontSize: 14,
    color: 'var(--color-on-surface)',
  },
  toggleDesc: {
    fontSize: 12,
    color: 'var(--color-on-surface-variant)',
    marginTop: 2,
  },
  saveBtn: {
    width: '100%',
    padding: '13px',
    borderRadius: 12,
    background: 'var(--color-primary)',
    color: 'white',
    fontSize: 15,
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
    transition: 'opacity 0.15s',
  },
};

/* ─── Sub-components ─────────────────────────────────────────────────── */

const SectionCard: React.FC<{
  label: string;
  sub?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}> = ({ label, sub, children, style }) => (
  <div style={{ ...S.sectionCard, ...style }}>
    <div style={S.sectionLabel}>{label}</div>
    {sub && <div style={S.sectionSub}>{sub}</div>}
    {children}
  </div>
);

const ChipRow: React.FC<{
  items: string[];
  selected: string;
  onSelect: (v: string) => void;
}> = ({ items, selected, onSelect }) => (
  <div style={S.chipRow}>
    {items.map(item => (
      <button key={item} onClick={() => onSelect(item)} style={chipStyle(selected === item)}>
        {item}
      </button>
    ))}
  </div>
);

const MultiChipRow: React.FC<{
  items: string[];
  selected: string[];
  onToggle: (v: string) => void;
}> = ({ items, selected, onToggle }) => (
  <div style={S.chipRow}>
    {items.map(item => (
      <button key={item} onClick={() => onToggle(item)} style={chipStyle(selected.includes(item))}>
        {item}
      </button>
    ))}
  </div>
);

const ToggleSwitch: React.FC<{
  label: string;
  desc?: string;
  value: boolean;
  onChange: (v: boolean) => void;
  last?: boolean;
}> = ({ label, desc, value, onChange, last }) => (
  <div style={last ? S.toggleRowLast : S.toggleRow}>
    <div>
      <div style={S.toggleLabel}>{label}</div>
      {desc && <div style={S.toggleDesc}>{desc}</div>}
    </div>
    <button
      onClick={() => onChange(!value)}
      style={swTrackStyle(value)}
      aria-checked={value}
      role="switch"
      aria-label={label}
    >
      <span style={swThumbStyle(value)} />
    </button>
  </div>
);

/* ─── API ↔ App field mapping ───────────────────────────────────────── */

// API response (lowercase keys) → Preferences (camelCase)
const mapApiPrefs = (raw: any): Preferences => ({
  id: raw.id,
  userId: raw.userid,
  userType: raw.usertype,
  type: raw.type ?? [],
  faithInclination: raw.faithinclination ?? '',
  ageGroup: raw.agegroup?.replace(/_/g, ' ') ?? '',
  expertise: raw.areaofexpertise ?? [],
  gender: raw.gender ?? '',
  accessibilityOption: raw.accessibilityoption ?? false,
  nickName: raw.nickname ?? '',
  reminders: raw.reminders ?? true,
  scheduleFor: raw.schedulefor ?? '',
  shareFeedback: raw.sharefeedback ?? false,
});


const toApiPayload = (p: Preferences) => ({
  id: p.id,
 userId: p.userId,      
  userType: p.userType,  
  type: p.type ?? [],
  faithInclination: p.faithInclination,
  agegroup: p.ageGroup,
  areaOfExpertise: p.expertise ?? [],
  gender: p.gender,
  accessibilityOption: p.accessibilityOption,
  nickName: p.nickName,
  reminders: p.reminders,
  scheduleFor: p.scheduleFor,
  shareFeedback: p.shareFeedback,
});

/* ─── Main page ──────────────────────────────────────────────────────── */

const PreferencePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = location.pathname === ROUTES.EDIT_PREFERENCE;
  const authStore = useAuthStore();
  const user = authStore.retrieveUser();
  const [loading, setLoading] = useState(false);
  const [fetchingExisting, setFetchingExisting] = useState(true);

  const [prefs, setPrefs] = useState<Preferences>({
    userId: user?.id,
    gender: '',
    ageGroup: '',
    nickName: '',
    scheduleFor: '',
    faithInclination: '',
    type: [],
    expertise: [],
    shareFeedback: false,
    reminders: true,
    accessibilityOption: false,
    userType: 'Customer',
  });

  useEffect(() => {
    const loadExisting = async () => {
      setFetchingExisting(true);
      try {
        if (user?.id) {
          const existing = await authStore.fetchPrefs(user.id);

if (existing) {
  setPrefs(p => ({
    ...p,
    ...existing,
    ageGroup: existing.ageGroup?.replace(/_/g, ' '),
  }));

  authStore.saveLocalPrefs(existing);
}
        }
      } catch {
        // no prefs yet — stay with defaults
      } finally {
        setFetchingExisting(false);
      }
    };
    loadExisting();
  }, []);

  const toggle = (field: 'type' | 'expertise', val: string) => {
    setPrefs(p => {
      const arr = p[field] || [];
      const updated = arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val];
      return { ...p, [field]: updated };
    });
  };

  const select = (field: keyof Preferences, val: string) =>
    setPrefs(p => ({ ...p, [field]: val }));

  const handleSave = async () => {
    if (!prefs.gender) { toast.error('Please select your preferred therapist gender.'); return; }
    if (!prefs.ageGroup) { toast.error('Please select your preferred therapist age group.'); return; }
    setLoading(true);
    try {
      // Guard: re-fetch the real id in case the initial load missed it
      let resolvedId = prefs.id;
      if (!resolvedId && user?.id) {
        const existing = await authStore.fetchPrefs(user.id);
        if (existing?.id) {
          resolvedId = existing.id;
          setPrefs(p => ({ ...p, id: resolvedId }));
        }
      }

      const payload = toApiPayload({ ...prefs, id: resolvedId, userId: user?.id });

      let rawResult: any;
      if (resolvedId) {
        rawResult = await authStore.editPrefs(payload);
      } else {
        rawResult = await authStore.savePrefs(payload);
      }

      // API returns lowercase keys too — map before storing
      const mappedResult = mapApiPrefs(rawResult);
      setPrefs(p => ({ ...p, ...mappedResult }));
      authStore.saveLocalPrefs(mappedResult);

      toast.success(resolvedId ? 'Preferences updated!' : 'Preferences saved!');
      navigate(ROUTES.INDEX, { replace: true });
    } catch {
      toast.error('Failed to save preferences.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={S.page}>
      {(loading || fetchingExisting) && <LoadingWidget />}

      {/* App bar */}
      <div style={S.appBar}>
        {prefs.id && (
          <button style={S.backBtn} onClick={() => navigate(-1)} aria-label="Go back">
            ←
          </button>
        )}
        <span style={S.appBarTitle}>
          {prefs.id ? 'Edit preferences' : 'Your preferences'}
        </span>
      </div>

      {/* Body */}
      <div style={S.body}>
        <p style={S.lead}>
          Help us match you with the right therapist by sharing your preferences.
        </p>

        {/* Row 1 — Nickname + Schedule for */}
        <div style={S.grid2}>
          <SectionCard label="Nickname" sub="What should your therapist call you?">
            <input
              style={S.textInput}
              placeholder="e.g. Alex"
              value={prefs.nickName || ''}
              onChange={e => setPrefs(p => ({ ...p, nickName: e.target.value }))}
            />
          </SectionCard>

          <SectionCard label="Schedule for" sub="Who is this session for?">
            <ChipRow
              items={scheduleFor}
              selected={prefs.scheduleFor || ''}
              onSelect={v => select('scheduleFor', v)}
            />
          </SectionCard>
        </div>

        {/* Row 2 — Therapist gender + Age group */}
        <div style={S.grid2}>
          <SectionCard label="Therapist gender" sub="Preferred gender of your therapist">
            <ChipRow
              items={genders}
              selected={prefs.gender || ''}
              onSelect={v => select('gender', v)}
            />
          </SectionCard>

          <SectionCard label="Therapist age group" sub="Preferred experience level">
            <ChipRow
              items={ageGroup}
              selected={prefs.ageGroup || ''}
              onSelect={v => select('ageGroup', v)}
            />
          </SectionCard>
        </div>

        {/* Faith inclination — full width */}
        <SectionCard label="Faith inclination" sub="Preferred religious background">
          <ChipRow
            items={religiousAffiliations}
            selected={prefs.faithInclination || ''}
            onSelect={v => select('faithInclination', v)}
          />
        </SectionCard>

        {/* Therapy approach — full width */}
        <SectionCard label="Therapy approach" sub="Select all that apply">
          <MultiChipRow
            items={therapyType}
            selected={prefs.type || []}
            onToggle={v => toggle('type', v)}
          />
        </SectionCard>

        {/* Expertise — full width */}
        <SectionCard label="Expertise needed" sub="Areas you'd like support with">
          <MultiChipRow
            items={expertiseIn}
            selected={prefs.expertise || []}
            onToggle={v => toggle('expertise', v)}
          />
        </SectionCard>

        {/* Toggles */}
        <div style={S.togglesCard}>
          <ToggleSwitch
            label="Session reminders"
            desc="Get notified before upcoming sessions"
            value={prefs.reminders !== false}
            onChange={v => setPrefs(p => ({ ...p, reminders: v }))}
          />
          <ToggleSwitch
            label="Share anonymous feedback"
            desc="Help us improve the service"
            value={prefs.shareFeedback || false}
            onChange={v => setPrefs(p => ({ ...p, shareFeedback: v }))}
          />
          <ToggleSwitch
            label="Accessibility options"
            desc="Enable screen reader and contrast improvements"
            value={prefs.accessibilityOption || false}
            onChange={v => setPrefs(p => ({ ...p, accessibilityOption: v }))}
            last
          />
        </div>

        <button style={S.saveBtn} onClick={handleSave} disabled={loading}>
          {prefs.id ? 'Update preferences' : 'Save & continue'}
        </button>
      </div>
    </div>
  );
};

export default PreferencePage;